import React, { useEffect, useMemo, useState } from 'react';
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import RadarRoundedIcon from '@mui/icons-material/RadarRounded';
import StorageRoundedIcon from '@mui/icons-material/StorageRounded';
import LanRoundedIcon from '@mui/icons-material/LanRounded';
import MemoryRoundedIcon from '@mui/icons-material/MemoryRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import { useNavigate, useParams } from 'react-router-dom';

import { BASEURL, PRIMARYCOLOR, COMPANY } from '../../../Constants';
import WhiteLabelForm from '../../../components/whiteLabelForm';
import Nav from '../nav';
import CoverPage from './coverPage';
import DisclaimerPage from './disclaimerPage';
import MethodologySection from './methodologyPage';
import {
  buildExternalReconReportPdfData,
  scoreColor,
} from './externalReconReportPdfHelpers';

import './ReportsPage.css';

const DEFAULT_PRIMARY = PRIMARYCOLOR || '#005CC5';
const DEFAULT_COMPANY = COMPANY || 'StealthNet AI';
const DEFAULT_CLIENT = '';

const downloadBlob = (blob, filename) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

const tocLinkSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  py: 0.85,
  color: '#10243E',
  textDecoration: 'none',
};

const MetricCard = ({ icon: Icon, label, value, caption, tone = DEFAULT_PRIMARY }) => (
  <Card
    sx={{
      p: 2.1,
      height: '100%',
      borderRadius: 3.5,
      boxShadow: 0,
      border: '1px solid',
      borderColor: alpha('#10243E', 0.08),
      bgcolor: '#fff',
    }}
  >
    <Stack direction="row" spacing={1.4} alignItems="flex-start">
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 2.5,
          bgcolor: alpha(tone, 0.1),
          color: tone,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 20 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', letterSpacing: 0.3 }}>
          {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 900, color: '#10243E', lineHeight: 1.1, mt: 0.35 }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.65, lineHeight: 1.5 }}>
          {caption}
        </Typography>
      </Box>
    </Stack>
  </Card>
);

const ScorePill = ({ score, label }) => (
  <Chip
    size="small"
    label={`${label} - ${score}`}
    sx={{
      bgcolor: alpha(scoreColor(score), 0.12),
      color: scoreColor(score),
      border: '1px solid',
      borderColor: alpha(scoreColor(score), 0.24),
      fontWeight: 800,
    }}
  />
);

const DataChips = ({ label, items = [], primary }) => (
  <Box sx={{ mt: 1.3 }}>
    <Typography variant="caption" sx={{ color: '#5C718E', letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
      {items.length ? items.map((item) => (
        <Chip
          key={`${label}-${item}`}
          size="small"
          label={item}
          variant="outlined"
          sx={{
            bgcolor: alpha(primary, 0.045),
            borderColor: alpha(primary, 0.16),
            '& .MuiChip-label': {
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            },
          }}
        />
      )) : (
        <Typography variant="body2" color="text.secondary">
          None observed in current recon data.
        </Typography>
      )}
    </Stack>
  </Box>
);

const TableOfContents = ({ items }) => (
  <Box sx={{ p: '0.8in 0.9in' }} id="table-of-contents">
    <Typography variant="h4" sx={{ fontWeight: 900, color: '#10243E', mb: 1 }}>
      Table of Contents
    </Typography>
    <Typography variant="body1" sx={{ color: '#51637D', lineHeight: 1.7, mb: 2.4 }}>
      This recon report is organized around external assets and the threat-model hypotheses attached to each asset.
    </Typography>

    {items.map((item) => (
      <Box key={item.id} component="a" href={`#${item.id}`} sx={tocLinkSx}>
        <Typography variant="body1" sx={{ fontWeight: 700 }}>
          {item.title}
        </Typography>
        <Box sx={{ flexGrow: 1, height: 1, bgcolor: '#dbe3ef' }} />
      </Box>
    ))}
  </Box>
);

const ExecutiveOverview = ({ report }) => {
  const cards = [
    {
      icon: StorageRoundedIcon,
      label: 'Assets Mapped',
      value: report.metrics.totalAssets,
      caption: `${report.metrics.assetsWithAttacks} asset${report.metrics.assetsWithAttacks === 1 ? '' : 's'} include modeled attack hypotheses.`,
      tone: report.brand.primary,
    },
    {
      icon: RadarRoundedIcon,
      label: 'Threat Hypotheses',
      value: report.metrics.totalHypotheses,
      caption: `${report.metrics.totalLikelyAttacks} reached the verification queue threshold.`,
      tone: '#C62828',
    },
    {
      icon: LanRoundedIcon,
      label: 'Open Ports',
      value: report.metrics.uniquePorts,
      caption: `${report.metrics.uniqueServices} unique service${report.metrics.uniqueServices === 1 ? '' : 's'} observed.`,
      tone: '#1C7C3F',
    },
    {
      icon: MemoryRoundedIcon,
      label: 'Tech Indicators',
      value: report.metrics.uniqueTechnologies,
      caption: 'Technology stack signals observed during external reconnaissance.',
      tone: '#7A2CB5',
    },
  ];

  return (
    <Box id="executive-summary" sx={{ p: '0.72in 0.82in', bgcolor: '#fff' }}>
      <Card
        sx={{
          p: 2.6,
          mb: 2.4,
          borderRadius: 3.5,
          boxShadow: 0,
          border: '1px solid',
          borderColor: alpha('#10243E', 0.08),
          background: `linear-gradient(180deg, ${alpha(report.brand.primary, 0.06)} 0%, rgba(255,255,255,0.98) 58%)`,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" sx={{ fontWeight: 900, color: '#10243E', mb: 0.5 }}>
              Executive Summary
            </Typography>
            <Typography variant="body1" sx={{ color: '#51637D', lineHeight: 1.6 }}>
              {report.organizationName} - {report.testingType} - ID #{report.pentestId}
            </Typography>
            <Typography variant="body1" sx={{ color: '#51637D', lineHeight: 1.6 }}>
              {report.reportDate}
            </Typography>
          </Box>

          <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Chip
              label={`GRADE: ${report.grade}`}
              sx={{
                bgcolor: report.gradeColor,
                color: '#fff',
                fontWeight: 900,
                borderRadius: '12px',
                mb: 0.8,
              }}
            />
            <Typography variant="body2" sx={{ color: '#51637D', lineHeight: 1.5 }}>
              Risk Score: <strong>{report.riskScore ?? '-'}</strong>
            </Typography>
            <Typography variant="body2" sx={{ color: '#51637D', lineHeight: 1.5 }}>
              Average top asset score: <strong>{report.metrics.averageTopScore}</strong>
            </Typography>
          </Box>
        </Stack>
      </Card>

      <Grid container spacing={2} sx={{ mb: 2.2 }}>
        {cards.map((card) => (
          <Grid key={card.label} item xs={12} sm={6}>
            <MetricCard {...card} />
          </Grid>
        ))}
      </Grid>

      <Box
        sx={{
          mb: 2.1,
          px: 2,
          py: 1.6,
          borderRadius: 3,
          bgcolor: alpha(report.brand.primary, 0.045),
          border: '1px solid',
          borderColor: alpha(report.brand.primary, 0.14),
        }}
      >
        <Typography variant="body1" sx={{ color: '#31445F', lineHeight: 1.7 }}>
          This recon report focuses on externally visible assets and threat-model hypotheses. It does not include
          exploit results, agent execution history, or confirmed findings.
        </Typography>
      </Box>

      <Stack spacing={1.4}>
        {report.executiveSummary.map((paragraph) => (
          <Typography key={paragraph} variant="body1" sx={{ color: '#31445F', lineHeight: 1.75 }}>
            {paragraph}
          </Typography>
        ))}
      </Stack>

      <Card
        sx={{
          mt: 2.5,
          p: 2.1,
          borderRadius: 3.5,
          boxShadow: 0,
          border: '1px solid',
          borderColor: alpha('#10243E', 0.08),
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 900, color: '#10243E', mb: 1.2 }}>
          Top At-Risk Assets
        </Typography>
        <Stack divider={<Divider flexItem />}>
          {report.topAssets.map((asset, index) => (
            <Stack key={asset.id} direction="row" spacing={1.4} alignItems="flex-start" sx={{ py: 1.05 }}>
              <Typography variant="body2" sx={{ color: '#51637D', fontWeight: 800, width: 20, pt: 0.2 }}>
                {index + 1}.
              </Typography>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 700, color: '#10243E', lineHeight: 1.5, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                >
                  {asset.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#51637D', lineHeight: 1.5 }}>
                  Top score {asset.score} - {asset.likelyAttackCount} verification-queue path{asset.likelyAttackCount === 1 ? '' : 's'} - {asset.totalAttackCount} total hypotheses - {asset.exposureCount} exposure signals
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      </Card>
    </Box>
  );
};

const AssetSection = ({ asset, report }) => (
  <Box id={asset.id} sx={{ p: '0.72in 0.82in', bgcolor: '#fff' }}>
    <Stack spacing={0.8} sx={{ mb: 1.9 }}>
      <Typography variant="overline" sx={{ color: '#5C718E', letterSpacing: 1.4, fontWeight: 800 }}>
        Asset {asset.ordinal}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 900, color: '#10243E' }}>
        Asset {asset.ordinal}
      </Typography>
      <Typography variant="body1" sx={{ color: '#51637D', lineHeight: 1.6 }}>
        The section below captures the observed external exposure and threat-model hypotheses associated to this asset.
      </Typography>
      <Box sx={{ width: 170, height: 4, borderRadius: '999px', bgcolor: report.brand.primary }} />
    </Stack>

    <Card
      sx={{
        p: 2.4,
        borderRadius: 3.5,
        boxShadow: 0,
        border: '1px solid',
        borderColor: alpha('#10243E', 0.08),
        bgcolor: alpha(report.brand.primary, 0.035),
        mb: 2,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'flex-start' }}
        sx={{ mb: 1.5 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 900, color: '#10243E', lineHeight: 1.2, mb: 0.6, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
          >
            {asset.title}
          </Typography>
          <Typography variant="body1" sx={{ color: '#51637D', lineHeight: 1.65 }}>
            {asset.assetTypeLabel}
          </Typography>
        </Box>

        <Chip
          icon={<SecurityRoundedIcon />}
          label={asset.assetTypeLabel}
          sx={{
            bgcolor: alpha(report.brand.primary, 0.1),
            color: report.brand.primaryMid,
            border: '1px solid',
            borderColor: alpha(report.brand.primary, 0.2),
            fontWeight: 800,
          }}
        />
      </Stack>

      <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#10243E', mb: 0.75 }}>
        Asset Summary
      </Typography>
      <Typography variant="body1" sx={{ color: '#31445F', lineHeight: 1.7, mb: 1.5 }}>
        {asset.summaryText}
      </Typography>

      <Grid container spacing={1.5} sx={{ mb: 0.8 }}>
        {[
          {
            label: 'Likelihood',
            value: asset.topAttackScore,
            note: asset.totalAttackCount ? `${asset.totalAttackCount} total hypotheses modeled` : 'No hypotheses recorded',
          },
          {
            label: 'Open Ports',
            value: asset.ports.length,
            note: asset.ports.length ? asset.ports.slice(0, 4).join(', ') : 'No ports captured',
          },
          {
            label: 'Tech Stack',
            value: asset.technologies.length,
            note: asset.services.length ? `${asset.services.length} service signals` : 'No service signals',
          },
        ].map((item) => (
          <Grid key={item.label} item xs={12} md={4}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 3,
                border: '1px solid',
                borderColor: alpha('#10243E', 0.08),
                bgcolor: '#fff',
                height: '100%',
              }}
            >
              <Typography variant="caption" sx={{ color: '#5C718E', letterSpacing: 0.5 }}>
                {item.label}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 900, color: '#10243E', mt: 0.3, mb: 0.45 }}>
                {item.value}
              </Typography>
              <Typography variant="body2" sx={{ color: '#51637D', lineHeight: 1.5 }}>
                {item.note}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      <DataChips label="Open Ports" items={asset.ports} primary={report.brand.primary} />
      <DataChips label="Services" items={asset.services} primary={report.brand.primary} />
      <DataChips label="Technology Stack" items={asset.technologies} primary={report.brand.primary} />
    </Card>

    <Stack spacing={1.5}>
      {asset.attacks.length ? asset.attacks.map((attack) => (
        <Card
          key={attack.id}
          sx={{
            p: 2.1,
            borderRadius: 3.5,
            boxShadow: 0,
            border: '1px solid',
            borderColor: alpha('#10243E', 0.08),
            bgcolor: '#fff',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.4}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'flex-start' }}
            sx={{ mb: 1.1 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 900, color: '#10243E', lineHeight: 1.35, mb: 0.45 }}>
                {attack.name}
              </Typography>
              <Typography variant="body2" sx={{ color: '#51637D', lineHeight: 1.55 }}>
                {attack.verified ? 'Queued for verification threshold' : 'Below active verification threshold'} - {attack.label} confidence
              </Typography>
            </Box>

            <ScorePill score={attack.score} label={attack.label} />
          </Stack>

          <Stack spacing={1.1}>
            {attack.description.map((paragraph) => (
              <Typography key={`${attack.id}-${paragraph}`} variant="body1" sx={{ color: '#31445F', lineHeight: 1.7 }}>
                {paragraph}
              </Typography>
            ))}
          </Stack>
        </Card>
      )) : (
        <Card
          sx={{
            p: 2.1,
            borderRadius: 3.5,
            boxShadow: 0,
            border: '1px solid',
            borderColor: alpha('#10243E', 0.08),
            bgcolor: alpha(report.brand.primary, 0.03),
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 900, color: '#10243E', mb: 0.75 }}>
            No attack hypotheses recorded
          </Typography>
          <Typography variant="body1" sx={{ color: '#31445F', lineHeight: 1.7 }}>
            No structured attack hypotheses were attached to this asset in the current threat-model dataset.
          </Typography>
        </Card>
      )}
    </Stack>
  </Box>
);

const ExternalReconReport = () => {
  const { pentest_id } = useParams();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [assets, setAssets] = useState([]);
  const [threatModel, setThreatModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY);
  const [companyName, setCompanyName] = useState(DEFAULT_COMPANY);
  const [clientName, setClientName] = useState(DEFAULT_CLIENT);

  const applyWhiteLabel = ({ primary, company, client }) => {
    if (primary !== undefined) setPrimaryColor(primary);
    if (company !== undefined) setCompanyName(company);
    if (client !== undefined) setClientName(client);
  };

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };

    const fetchJson = (url) => fetch(url, { headers }).then((response) => response.json());

    Promise.all([
      fetchJson(`${BASEURL}/pentest/stats?pentest_id=${pentest_id}`),
      fetchJson(`${BASEURL}/pentest/assets?pentest_id=${pentest_id}`),
      fetchJson(`${BASEURL}/pentests/external/threat_model?pentest_id=${pentest_id}`),
    ])
      .then(([statsRes, assetsRes, threatRes]) => {
        if ([statsRes, assetsRes, threatRes].some((result) => result?.message === 'Invalid token')) {
          navigate('/');
          return;
        }

        setStats(statsRes?.stats || statsRes || {});
        setAssets(Array.isArray(assetsRes?.assets) ? assetsRes.assets : []);
        setThreatModel(threatRes?.threat_model || threatRes || null);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [navigate, pentest_id]);

  const report = useMemo(() => {
    if (!stats) return null;

    return buildExternalReconReportPdfData({
      pentestId: pentest_id,
      primaryColor,
      companyName,
      clientName,
      stats,
      assets,
      threatModel,
    });
  }, [assets, clientName, companyName, pentest_id, primaryColor, stats, threatModel]);

  const handleDownloadPDF = async () => {
    if (!report) return;

    setDownloading(true);
    try {
      const [{ pdf }, { default: ExternalReconReportPdfDocument }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('./externalReconReportPdf'),
      ]);

      const blob = await pdf(<ExternalReconReportPdfDocument report={report} />).toBlob();
      downloadBlob(blob, `external_${pentest_id}_recon_report.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  if (loading || !report) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <Nav />
        <Button
          variant="contained"
          onClick={handleDownloadPDF}
          sx={{
            position: 'absolute',
            top: 8,
            right: 16,
            backgroundColor: DEFAULT_PRIMARY,
            '&:hover': {
              backgroundColor: darken(DEFAULT_PRIMARY, 0.12),
            },
          }}
        >
          Download PDF
        </Button>
      </Box>

      <Backdrop open={downloading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Card
          className="report-config-card"
          sx={{
            width: '100%',
            maxWidth: '8.5in',
            mb: 2,
            boxShadow: 'none',
            border: '1px solid rgba(0,0,0,0.08)',
          }}
        >
          <WhiteLabelForm
            defaultPrimary={DEFAULT_PRIMARY}
            defaultCompany={DEFAULT_COMPANY}
            defaultClient={DEFAULT_CLIENT}
            onApply={applyWhiteLabel}
            showCompany
            showClient
            title="White Label"
          />
        </Card>
      </Box>

      <Box className="report-platform-shell" sx={{ p: 2, '& > [aria-label^="pdf-page-"]': { mb: 2.5 } }}>
        <Box className="pdf-page cover-page" sx={{ display: 'flex', justifyContent: 'center' }} aria-label="pdf-page-0">
          <Card
            className="report-page-card report-page-card--cover"
            sx={{
              width: '100%',
              maxWidth: '8.5in',
              height: '10.8in',
              m: 0,
              p: 0,
              border: 0,
              borderRadius: 0,
              boxShadow: 'none',
            }}
          >
            <CoverPage
              primary={primaryColor}
              company={companyName}
              clientName={report.clientName}
              reportDate={report.reportDate}
              eyebrow={report.cover.eyebrow}
              title={report.cover.title}
              subtitle={report.cover.subtitle}
              tags={report.cover.tags}
            />
          </Card>
        </Box>

        <Box className="pdf-page" sx={{ display: 'flex', justifyContent: 'center' }} aria-label="pdf-page-1">
          <Card className="report-page-card report-page-card--standard" sx={{ width: '100%', maxWidth: '8.5in' }}>
            <DisclaimerPage primary={primaryColor} company={companyName} />
          </Card>
        </Box>

        <Box className="pdf-page" sx={{ display: 'flex', justifyContent: 'center' }} aria-label="pdf-page-2">
          <Card className="report-page-card report-page-card--standard" sx={{ width: '100%', maxWidth: '8.5in' }}>
            <CardContent sx={{ p: 0 }}>
              <TableOfContents items={report.tocItems} />
            </CardContent>
          </Card>
        </Box>

        <Box className="pdf-page" sx={{ display: 'flex', justifyContent: 'center' }} aria-label="pdf-page-3">
          <Card className="report-page-card report-page-card--standard" sx={{ width: '100%', maxWidth: '8.5in' }}>
            <ExecutiveOverview report={report} />
          </Card>
        </Box>

        <Box className="pdf-page" sx={{ display: 'flex', justifyContent: 'center' }} aria-label="pdf-page-4">
          <Card className="report-page-card report-page-card--standard" sx={{ width: '100%', maxWidth: '8.5in' }}>
            <MethodologySection
              primary={primaryColor}
              title="Methodology"
              intro="This section explains how the assessment team normally approaches external recon from scope review through service discovery and threat modeling."
              summary="We use a staged recon methodology so each phase builds on verified observations from the phase before it, allowing the assessment team to move from broad exposure mapping into asset-specific threat modeling."
              paragraphs={report.methodology}
            />
          </Card>
        </Box>

        {report.assetRows.map((asset, index) => (
          <Box
            key={asset.id}
            className="pdf-page"
            sx={{ display: 'flex', justifyContent: 'center' }}
            aria-label={`pdf-page-${index + 5}`}
          >
            <Card className="report-page-card report-page-card--standard" sx={{ width: '100%', maxWidth: '8.5in' }}>
              <AssetSection asset={asset} report={report} />
            </Card>
          </Box>
        ))}
      </Box>
    </>
  );
};

export default ExternalReconReport;
